import React, { useState, useEffect } from 'react';
import { MenuIcon, BellIcon, ContactIcon, UserGroupIcon } from './icons';
import notificationService from '../services/notificationService.js';
import LogoComponent from '../../shared/LogoComponent.js';
import { useTheme } from '../contexts/ThemeContext';
import { sendBusinessPlanToTelegram } from '../services/businessPlanTelegramService.js';
import { useAuth } from '../../../contexts/AuthContext';

const CONTACTS_STORAGE_KEY = 'busin.us-contacts';
const BUSINESS_PLAN_STORAGE_KEY = 'busin.us-business-plan';

const Header = ({
  onGenerate,
  isLoading,
  onOpenNotifications,
  // Workspace props
  workspaces = [],
  currentWorkspace = null,
  onCreateNewWorkspace,
  onSwitchWorkspace,
  onDeleteWorkspace,
  onImportToNewWorkspace,
  // Add nodes, edges, groups for Telegram export
  nodes = [],
  edges = [],
  groups = [],
  // Business plan text to share with sidebar
  businessPlanText = '',
  setBusinessPlanText = () => { },
  onInviteCollaborator = async () => { },
  session = null
}) => {
  const [prompt, setPrompt] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showNewWorkspaceForm, setShowNewWorkspaceForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [isInviting, setIsInviting] = useState(false);
  // Add contact management states
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', role: '' });
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { currentTheme, setTheme, themes } = useTheme();
  const { user, logout } = useAuth();

  // Load contacts from localStorage
  useEffect(() => {
    try {
      const savedContacts = localStorage.getItem(CONTACTS_STORAGE_KEY);
      if (savedContacts) {
        setContacts(JSON.parse(savedContacts));
      }
    } catch (e) {
      console.error("Failed to load contacts from localStorage", e);
    }
  }, []);

  // Save contacts to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CONTACTS_STORAGE_KEY, JSON.stringify(contacts));
    } catch (e) {
      console.error("Failed to save contacts to localStorage", e);
    }
  }, [contacts]);

  // Load business plan text from localStorage on mount
  useEffect(() => {
    try {
      const savedBusinessPlan = localStorage.getItem(BUSINESS_PLAN_STORAGE_KEY);
      if (savedBusinessPlan) {
        setBusinessPlanText(savedBusinessPlan);
        setPrompt(savedBusinessPlan);
      }
    } catch (e) {
      console.error("Failed to load business plan from localStorage", e);
    }
  }, [setBusinessPlanText]);

  // Update prompt when businessPlanText changes
  useEffect(() => {
    setPrompt(businessPlanText || ''); // Ensure we have a string
  }, [businessPlanText]);

  useEffect(() => {
    // Subscribe to notification updates to show unread count
    const listener = (notifications) => {
      const count = notifications.filter(n => !n.read).length;
      setUnreadCount(count);
    };

    notificationService.addListener(listener);

    // Get initial unread count
    setUnreadCount(notificationService.getUnreadCount());

    return () => {
      notificationService.removeListener(listener);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const promptValue = prompt || ''; // Ensure we have a string
    if (promptValue.trim() && !isLoading) {
      // Save business plan text to localStorage
      try {
        localStorage.setItem(BUSINESS_PLAN_STORAGE_KEY, promptValue);
      } catch (e) {
        console.error("Failed to save business plan to localStorage", e);
      }

      // Update shared business plan text
      setBusinessPlanText(promptValue);

      // Generate workflow
      onGenerate(promptValue);
    }
  };

  const handleCreateWorkspace = () => {
    const newWorkspaceNameValue = newWorkspaceName || ''; // Ensure we have a string
    if (newWorkspaceNameValue.trim()) {
      onCreateNewWorkspace(newWorkspaceNameValue.trim());
      setNewWorkspaceName('');
      setShowNewWorkspaceForm(false);
      setShowWorkspaceMenu(false);
    }
  };

  // Contact management functions
  const handleAddContact = () => {
    if (newContact.name.trim()) {
      const contact = {
        id: `contact_${Date.now()}`,
        ...newContact
      };
      setContacts(prev => [...prev, contact]);
      setNewContact({ name: '', email: '', role: '' });
    }
  };

  const handleDeleteContact = (id) => {
    setContacts(prev => prev.filter(contact => contact.id !== id));
  };

  const handleInvite = async () => {
    if (inviteEmail.trim() && !isInviting) {
      setIsInviting(true);
      try {
        await onInviteCollaborator(currentWorkspace.id, inviteEmail.trim(), inviteRole);
        setInviteEmail('');
        setShowInviteModal(false);
        alert(`Invitation sent to ${inviteEmail}`);
      } catch (err) {
        alert('Failed to send invitation');
      } finally {
        setIsInviting(false);
      }
    }
  };

  return (
    <header className="bg-dark-surface border-b border-dark-border p-4 flex items-center justify-between relative">
      <div className="flex items-center gap-4">
        <button className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg sm:hidden">
          <MenuIcon className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <LogoComponent size="md" includeAudio={false} />
          <h1 className="text-xl font-bold text-dark-text-primary">Business Control Room</h1>
        </div>

        {/* Workspace selector */}
        <div className="relative">
          <button
            onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
            className="flex items-center gap-2 px-3 py-1.5 bg-dark-bg border border-dark-border rounded-md text-dark-text-primary hover:bg-dark-surface"
          >
            <span className="max-w-[120px] truncate">
              {currentWorkspace?.name || 'Select Workspace'}
            </span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showWorkspaceMenu && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-dark-surface border border-dark-border rounded-md shadow-lg z-50">
              <div className="py-1">
                {workspaces.map(workspace => (
                  <button
                    key={workspace.id}
                    onClick={() => {
                      onSwitchWorkspace(workspace.id);
                      setShowWorkspaceMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${currentWorkspace?.id === workspace.id
                      ? 'bg-brand-primary/20 text-brand-primary'
                      : 'text-dark-text-primary hover:bg-dark-bg'
                      }`}
                  >
                    <span className="truncate">{workspace.name}</span>
                    {workspaces.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteWorkspace(workspace.id);
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </button>
                ))}

                <div className="border-t border-dark-border my-1"></div>

                {showNewWorkspaceForm ? (
                  <div className="px-4 py-2">
                    <input
                      type="text"
                      value={newWorkspaceName || ''} // Ensure we have a string
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      placeholder="Workspace name"
                      className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-1.5 text-sm text-dark-text-primary mb-2"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateWorkspace}
                        className="flex-1 bg-brand-primary text-white rounded-md px-3 py-1.5 text-sm"
                      >
                        Create
                      </button>
                      <button
                        onClick={() => {
                          setShowNewWorkspaceForm(false);
                          setNewWorkspaceName('');
                        }}
                        className="flex-1 bg-dark-bg border border-dark-border text-dark-text-primary rounded-md px-3 py-1.5 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewWorkspaceForm(true)}
                    className="w-full text-left px-4 py-2 text-sm text-dark-text-primary hover:bg-dark-bg flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Workspace
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 max-w-2xl mx-4">
        <div className="relative">
          <input
            type="text"
            value={prompt || ''} // Ensure we have a string
            onChange={(e) => {
              const value = e.target.value || ''; // Ensure we have a string
              setPrompt(value);
              setBusinessPlanText(value);
            }}
            placeholder="Describe your business plan..."
            disabled={isLoading}
            className="w-full bg-dark-bg border border-dark-border rounded-lg py-2 pl-4 pr-32 text-dark-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
            {prompt && (
              <button
                type="button"
                onClick={() => {
                  setPrompt('');
                  setBusinessPlanText('');
                }}
                className="p-1.5 rounded-md text-dark-text-secondary hover:bg-red-500 hover:text-white transition-colors"
                title="Clear"
                disabled={isLoading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              type="submit"
              disabled={!prompt || !prompt.trim() || isLoading} // Added null check
              className="bg-brand-primary text-white rounded-md px-3 py-1 text-sm hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : 'Generate'}
            </button>
          </div>
        </div>
      </form>

      <div className="flex items-center gap-2">
        {currentWorkspace && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-md hover:bg-emerald-600/30 transition-colors"
          >
            <UserGroupIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Share</span>
          </button>
        )}

        {/* Contact Management Button */}
        <button
          className="p-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-primary hover:bg-dark-bg transition-colors flex items-center justify-center"
          onClick={() => setShowContactPanel(!showContactPanel)}
          title="Manage Contacts"
        >
          <ContactIcon className="w-5 h-5" />
        </button>

        {/* Theme Selector Button */}
        <div className="relative">
          <button
            className="p-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-primary hover:bg-dark-bg transition-colors flex items-center justify-center"
            onClick={() => setShowThemeSelector(!showThemeSelector)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
            </svg>
          </button>

          {showThemeSelector && (
            <div className="absolute right-0 mt-2 w-48 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
              <div className="p-2">
                <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider mb-2 px-2">Themes</h3>
                {Object.entries(themes).map(([key, theme]) => (
                  <div
                    key={key}
                    onClick={() => {
                      setTheme(key);
                      setShowThemeSelector(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors cursor-pointer ${currentTheme === key
                      ? 'bg-brand-primary/20 text-brand-primary'
                      : 'text-dark-text-primary hover:bg-dark-bg'
                      }`}
                  >
                    <span className="flex-shrink-0 w-3 h-3 rounded-full" style={{ backgroundColor: theme.colors['brand-primary'] }}></span>
                    {theme.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Click outside to close theme selector */}
          {showThemeSelector && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowThemeSelector(false)}
            ></div>
          )}
        </div>

        {/* Notification Button */}
        <button
          className="relative p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg flex items-center justify-center"
          onClick={onOpenNotifications}
        >
          <BellIcon className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>

        {/* User Profile Dropdown */}
        {user && (
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-primary/20 border border-brand-primary/30 text-brand-primary hover:bg-brand-primary/30 transition-colors"
              title={`Logged in as ${user.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-medium truncate max-w-[150px]">{user.name?.split(' ')[0]}</span>
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-dark-border">
                  <div className="text-sm font-semibold text-dark-text-primary">{user.name}</div>
                  <div className="text-xs text-dark-text-secondary mt-1">{user.email}</div>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/20 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Click outside to close profile menu */}
            {showProfileMenu && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowProfileMenu(false)}
              ></div>
            )}
          </div>
        )}
      </div>

      {/* Contact Management Panel */}
      {showContactPanel && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-dark-text-primary">Manage Contacts</h3>
              <button
                onClick={() => setShowContactPanel(false)}
                className="p-1 rounded-md text-dark-text-secondary hover:bg-dark-bg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Add Contact Form */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-dark-text-secondary mb-2">Add New Contact</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Name"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-1.5 text-sm text-dark-text-primary"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-1.5 text-sm text-dark-text-primary"
                />
                <input
                  type="text"
                  placeholder="Role"
                  value={newContact.role}
                  onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                  className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-1.5 text-sm text-dark-text-primary"
                />
                <button
                  onClick={handleAddContact}
                  className="w-full bg-brand-primary text-white rounded-md px-3 py-1.5 text-sm hover:bg-brand-primary/90"
                >
                  Add Contact
                </button>
              </div>
            </div>

            {/* Contacts List */}
            <div>
              <h4 className="text-sm font-medium text-dark-text-secondary mb-2">Your Contacts</h4>
              <div className="max-h-60 overflow-y-auto">
                {contacts.length > 0 ? (
                  <ul className="space-y-2">
                    {contacts.map(contact => (
                      <li
                        key={contact.id}
                        className="flex items-center justify-between p-2 bg-dark-bg rounded-md"
                      >
                        <div>
                          <div className="font-medium text-dark-text-primary">{contact.name}</div>
                          {contact.email && <div className="text-xs text-dark-text-secondary">{contact.email}</div>}
                          {contact.role && <div className="text-xs text-dark-text-secondary">{contact.role}</div>}
                        </div>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-dark-text-secondary text-center py-4">No contacts added yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close contact panel */}
      {showContactPanel && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowContactPanel(false)}
        ></div>
      )}

      {/* Click outside to close workspace menu */}
      {showWorkspaceMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowWorkspaceMenu(false);
            setShowNewWorkspaceForm(false);
            setNewWorkspaceName('');
          }}
        ></div>
      )}
      {/* Invite Collaborator Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowInviteModal(false)}></div>
          <div className="relative w-full max-w-md bg-dark-surface border border-dark-border rounded-xl shadow-2xl p-6">
            <h3 className="text-xl font-bold text-dark-text-primary mb-4">Invite Collaborator</h3>
            <p className="text-dark-text-secondary text-sm mb-6 text-dark-text-primary">Share "{currentWorkspace?.name}" with others via email.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-dark-text-secondary uppercase mb-1">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-4 py-2.5 text-dark-text-primary focus:ring-1 focus:ring-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-dark-text-secondary uppercase mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-4 py-2.5 text-dark-text-primary focus:ring-1 focus:ring-brand-primary"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-dark-border text-dark-text-primary hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || isInviting}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-brand-primary text-white font-semibold hover:bg-brand-primary/90 disabled:opacity-50 transition-colors"
                >
                  {isInviting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;